// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/lib/houseTimes.ts
// 🔧 PURPOSE: GLI ORARI DI CHECK-IN E DI CHECK-OUT che l'ospite legge in
//             app, da una fonte sola: quelli che manda il channel manager
//             (Impostazioni della struttura, più il late check-out o il
//             check-in anticipato COMPRATI). Home, "Il mio soggiorno" ed
//             Extra chiedono tutti qui, così nessuna schermata scrive
//             un'ora diversa da quella con cui il server apre e chiude la
//             casa (porta, clima, lavatrice, extra).
//
//             15:00 e 10:00 restano soltanto come ultima risposta: per un
//             channel manager vecchio che gli orari non li manda ancora, o
//             per una sessione salvata prima che arrivassero.
// ============================================

import type { ExtrasCatalog, GuestBooking, PurchasedExtra } from './guestApi';

/** L'ora di check-in quando il channel manager non ne manda una. */
export const DEFAULT_CHECK_IN_TIME = '15:00';
/** L'ora di check-out quando il channel manager non ne manda una. */
export const DEFAULT_CHECK_OUT_TIME = '10:00';

/** "9:30" → "09:30"; tutto quello che non è un orario vero → null. */
export function validTime(value: unknown): string | null {
  const m = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(String(value ?? '').trim());
  return m ? `${m[1].padStart(2, '0')}:${m[2]}` : null;
}

/** "15:30" → 930 (minuti dalla mezzanotte); un orario scritto male → null. */
export function timeMinutes(value: unknown): number | null {
  const t = validTime(value);
  if (!t) return null;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

type Bought = Pick<PurchasedExtra, 'serviceId' | 'selectedTime'>;

/** Quello che serve della prenotazione (tutto facoltativo: sessioni vecchie, test). */
export type HouseTimesBooking = Partial<Pick<GuestBooking, 'checkInTime' | 'checkOutTime' | 'customCheckOutTime'>> & {
  purchasedExtras?: Bought[] | null;
};

/** Quello che serve del catalogo degli extra. */
export type HouseTimesCatalog = Partial<Pick<ExtrasCatalog, 'checkInTime' | 'checkOutTime' | 'customCheckOutTime' | 'lateCheckout'>> & {
  purchasedExtras?: Bought[] | null;
};

export interface HouseTimesSources {
  booking?: HouseTimesBooking | null;
  catalog?: HouseTimesCatalog | null;
}

const firstValid = (...values: unknown[]): string | null => {
  for (const v of values) {
    const t = validTime(v);
    if (t) return t;
  }
  return null;
};

/** Gli extra comprati: il catalogo è più fresco della sessione salvata. */
const boughtTime = (src: HouseTimesSources, serviceId: string): string | null => {
  const list = src.catalog?.purchasedExtras || src.booking?.purchasedExtras || [];
  return validTime(list.find((e) => e.serviceId === serviceId)?.selectedTime);
};

/**
 * L'orario di uscita VERO: il late check-out comprato, poi quello che il
 * channel manager ha già deciso per la prenotazione (`checkOutTime`: late
 * check-out, altrimenti le Impostazioni), poi l'orario standard dell'offerta
 * del late check-out, e solo alla fine le 10:00. Il catalogo viene prima
 * della sessione: si rilegge più spesso, e se in Impostazioni l'ora cambia
 * lo sa per primo.
 */
export function resolveCheckOutTime(src: HouseTimesSources | null | undefined): string {
  const s = src || {};
  return boughtTime(s, 'late_checkout')
    || firstValid(
      s.booking?.customCheckOutTime,
      s.catalog?.customCheckOutTime,
      s.catalog?.checkOutTime,
      s.booking?.checkOutTime,
      s.catalog?.lateCheckout?.fromTime,
    )
    || DEFAULT_CHECK_OUT_TIME;
}

/**
 * L'ora di check-in della struttura (Impostazioni), senza il check-in
 * anticipato: è quella da scrivere nell'offerta "Entra prima delle …".
 */
export function resolveHouseCheckInTime(src: HouseTimesSources | null | undefined): string {
  const s = src || {};
  return firstValid(s.catalog?.checkInTime, s.booking?.checkInTime) || DEFAULT_CHECK_IN_TIME;
}

/**
 * L'ora da cui l'ospite entra davvero: quella della struttura, oppure il
 * check-in anticipato comprato se è prima (la stessa regola della serratura
 * nel channel manager, integrations/switchbot-passcode-window).
 */
export function resolveCheckInTime(src: HouseTimesSources | null | undefined): string {
  const s = src || {};
  const house = resolveHouseCheckInTime(s);
  const early = boughtTime(s, 'early_checkin');
  return early && early < house ? early : house;
}
